<template>
  <div class="card product-card" data-panel-group>
    <div class="card-header" :id="headingId">
      <button
        class="btn-accordion"
        :class="[statusKey, { collapsed: !isOpen }]"
        type="button"
        v-b-toggle="collapseId"
        :aria-expanded="String(isOpen)"
        :aria-controls="collapseId"
      >
        <span v-if="statusKey === 'approved'" class="approved-text">
          Congratulations! This offer is approved by {{ approvedOfferBuyerName }}
        </span>
        <h3 class="market-project__title">
          <a href="#" @click.stop.prevent="emitView">
            {{ productTitle }}
          </a>
          <MarketplaceUnreadBadge :count="productBulbCount" />
        </h3>
        <div v-if="marketplaceName">
          Marketplace: <strong>{{ marketplaceName }}</strong>
        </div>
        <ul class="market-project__category">
          <li v-if="countryName">{{ countryName }}</li>
          <li v-for="item in categorySpecItems" :key="item.key">{{ item.value }}</li>
        </ul>
        <ul class="offers__header">
          <li><label>Price:</label><strong>{{ priceText }}</strong></li>
          <li><label>Delivery time:</label><strong>{{ deliveryDateText }}</strong></li>
          <li><label>Status:</label><span class="status" :class="statusKey">{{ statusDisplayText }}</span></li>
        </ul>
        <ul class="offers-action">
          <template v-if="statusKey === 'draft'">
            <li>
              <a
                href="#"
                class="btn-action"
                @click.stop.prevent="emitFinalizeDraft"
              >
                <i class="icon-final"></i>
                Finalize draft
              </a>
            </li>
            <li>
              <a
                href="#"
                class="btn-action"
                @click.stop.prevent="emitView"
              >
                <i class="icon-info"></i>
                See details
              </a>
            </li>
          </template>

          <template v-else>
            <li v-if="hasMessages">
              <a
                href="#"
                class="btn-action"
                data-toggle-panel="seller"
                :data-panel-unread="productMessagesCount"
              >
                <i class="icon-chat"></i>
                Messages
                <MarketplaceUnreadBadge :count="productMessagesCount" />
              </a>
            </li>

            <li v-if="hasCounterOffers">
              <a
                href="#"
                class="btn-action"
                data-toggle-panel="offer"
              >
                <i class="icon-coffer"></i>
                Counter Offer
                <MarketplaceUnreadBadge :count="counterOfferBulbCount" />
              </a>
            </li>

            <li v-if="canEdit">
              <a
                href="#"
                class="btn-action"
                @click.stop.prevent="emitEdit"
              >
                <i class="icon-edit2"></i>
                Edit product
              </a>
            </li>

            <li>
              <a
                href="#"
                class="btn-action"
                @click.stop.prevent="emitView"
              >
                <i class="icon-info"></i>
                See details
              </a>
            </li>
          </template>
        </ul>
      </button>
    </div>

    <ProductMessagesPanel
      v-if="hasMessages"
      ref="productMessagesPanel"
      :productId="product.id"
      :messages="liveProductMessages"
    />

    <ProductCounterOffersPanel
      v-if="hasCounterOffers"
      :productId="product.id"
      :marketplaceId="marketplaceId"
      role="seller"
      :offers="product.offers"
      :canApproveOffers="statusKey !== 'approved'"
      @approve="$emit('approve', $event)"
    />

    <BCollapse
      :id="collapseId"
      accordion="accordionOffers"
      class="accordion-sublevel"
      @shown="isOpen = true"
      @hidden="isOpen = false"
    >
      <div class="card-body">
        <h5>Product details:</h5>
        <p>{{ safeText(product.detail) }}</p>

        <h5>Product advantages/features:</h5>
        <p class="mb-0">{{ safeText(product.feature) }}</p>
      </div>
    </BCollapse>
  </div>
</template>

<script>
import { BCollapse } from 'bootstrap-vue';
import ProductMessagesPanel from '@/components/matchmaking/ProductMessagesPanel.vue';
import ProductCounterOffersPanel from '@/components/matchmaking/ProductCounterOffersPanel.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import marketplaceProductMessages from '@/mixins/marketplaceProductMessages';

export default {
  name: 'SellerProductCard',
  components: {
    BCollapse,
    ProductMessagesPanel,
    ProductCounterOffersPanel,
    MarketplaceUnreadBadge,
  },

  mixins: [marketplaceProductMessages],

  props: {
    product: { type: Object, required: true },
    marketplaceDef: { type: Object, default: null },
    statusResolver: { type: Function, default: null },
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    headingId() {
      return `headingOffers-${this.productId}`;
    },
    collapseId() {
      return `collapse-offer${this.productId}`;
    },
    productId() {
      // eslint-disable-next-line no-underscore-dangle
      return this.product && this.product.id ? String(this.product.id) : String(this._uid);
    },

    marketplaceId() {
      const p = this.product || {};
      return p.marketplace_id ?? (p.marketplace && p.marketplace.id) ?? null;
    },

    productBulbCount() {
      return this.$store.getters.productBulb('seller', this.product && this.product.id);
    },

    mpMessageRole() {
      return 'seller';
    },
    mpMessageProductId() {
      return this.product && this.product.id;
    },
    mpBaseMessages() {
      return Array.isArray(this.product && this.product.messages) ? this.product.messages : [];
    },
    mpProductOffers() {
      return Array.isArray(this.product && this.product.offers) ? this.product.offers : [];
    },

    productTitle() {
      return this.product && this.product.name ? String(this.product.name) : '—';
    },

    marketplaceName() {
      if (this.marketplaceDef && this.marketplaceDef.name) return this.marketplaceDef.name;
      if (this.product && this.product.marketplace
        && this.product.marketplace.name) return this.product.marketplace.name;
      return '';
    },

    countryName() {
      const cc = this.product && this.product.company_country;
      if (!cc) return '';
      if (typeof cc === 'object') return cc.name || cc.iso || '';
      return String(cc);
    },

    deliveryDateText() {
      const raw = this.product && (this.product.deadline_at
        || this.product.delivery_date || this.product.deliver_date);
      if (!raw) return 'not specified';
      return String(raw).trim() || 'not specified';
    },

    statusKey() {
      if (typeof this.statusResolver === 'function') {
        const s = this.statusResolver(this.product);
        if (s) return s;
      }

      const p = this.product || {};

      if (p.status_name) {
        const s = String(p.status_name).toLowerCase();
        if (s.indexOf('draft') !== -1) return 'draft';
        if (s.indexOf('approved') !== -1) return 'approved';
        if (s.indexOf('declined') !== -1) return 'declined';
        if (s.indexOf('counter') !== -1) return 'counteroffered';
        if (s.indexOf('thread') !== -1) return 'threads';
        if (s.indexOf('saved') !== -1) return 'saved';
        return 'offered';
      }

      const n = Number(p.status);
      if (n === 3) return 'draft';
      if (n === 2) return 'approved';
      if (n === 4) return 'declined';

      if (this.hasCounterOffersStatus) return 'counteroffered';
      if (p.saved) return 'saved';

      return 'offered';
    },

    statusLabel() {
      const map = {
        offered: 'Offered',
        saved: 'Saved',
        draft: 'Draft',
        threads: 'Message threads',
        approved: 'Approved',
        counteroffered: 'Counter offered',
        declined: 'Declined',
      };
      return map[this.statusKey] || 'Offered';
    },

    statusDisplayText() {
      return this.statusKey === 'approved' ? 'approved' : this.statusLabel;
    },

    approvedOfferBuyerName() {
      const offers = this.product?.offers;
      if (!Array.isArray(offers)) return "Buyer's Name";

      const approved = offers.find((o) => {
        const status = o?.status;
        if (Array.isArray(status)) {
          const active = status.find((s) => s && s.active);
          if (active) {
            const name = String(active.name || '').toLowerCase();
            return name.includes('approved') || Number(active.id) === 3 || Number(active.id) === 2;
          }
        }
        if (status && typeof status === 'object' && !Array.isArray(status)) {
          const name = String(status.name || '').toLowerCase();
          return name.includes('approved') || Number(status.id) === 3 || Number(status.id) === 2;
        }
        return Number(o?.status) === 3 || Number(o?.status) === 2;
      });

      if (approved?.user?.name) return approved.user.name;
      return "Buyer's Name";
    },

    hasMessages() {
      const live = Array.isArray(this.liveProductMessages) ? this.liveProductMessages.length : 0;
      const base = Array.isArray(this.product && this.product.messages)
        ? this.product.messages.length : 0;
      return (live || base) > 0 || this.productMessagesCount > 0;
    },

    // UI rule: show Counter Offer button/panel only when backend returns offers in array
    hasCounterOffers() {
      const p = this.product || {};
      return Array.isArray(p.offers) && p.offers.length > 0;
    },

    // Keep status badge more robust (in case API sends only counts)
    hasCounterOffersStatus() {
      const p = this.product || {};

      const hasCounterOffersCount = Number(p.counter_offers_count || 0) > 0;
      const hasOffersCount = Number(p.offers_count || 0) > 0;
      const isCounterOfferedFlag = Boolean(p.counteroffered || p.counter_offered);

      return Boolean(
        this.hasCounterOffers
        || hasCounterOffersCount
        || hasOffersCount
        || isCounterOfferedFlag,
      );
    },

    canEdit() {
      return this.statusKey !== 'approved' && this.statusKey !== 'declined';
    },

    specItems() {
      const p = this.product || {};
      const data = Array.isArray(p.data) ? p.data : [];

      const items = [];

      data.forEach((field) => {
        if (!field || !field.name) return;

        const value = this.formatFieldValue(field);
        if (!value) return;

        items.push({
          key: String(field.id || field.name),
          label: String(field.name),
          value,
        });
      });

      return items.slice(0, 10);
    },

    categorySpecItems() {
      return this.specItems.slice(0, 2);
    },

    priceText() {
      const price = this.product?.price;
      const currency = this.product?.currency || 'euro';
      if (price === null || price === undefined || price === '' || Number(price) === 0) {
        return '—';
      }
      return `${price} ${currency}`;
    },
  },

  methods: {
    safeText(v) {
      const s = String(v || '').trim();
      return s || '—';
    },

    formatFieldValue(field) {
      const raw = field.initValue;

      if (raw === null || raw === undefined || raw === '') return '';

      if (field.type !== 'dropbox') {
        return String(raw);
      }

      const mapOne = (id) => {
        if (id === null || id === undefined || id === '') return '';

        if (field.values && typeof field.values === 'object') {
          if (Array.isArray(field.values)) {
            const found = field.values.find((v) => String(v.id) === String(id));
            if (found) return String(found.value || found.name || id);
          } else {
            const key = String(id);
            if (field.values[key] !== undefined) return String(field.values[key]);
          }
        }

        return String(id);
      };

      if (field.multiple) {
        const arr = Array.isArray(raw) ? raw : [raw];
        return arr.map(mapOne).filter(Boolean).join(', ');
      }

      return mapOne(raw);
    },

    emitEdit() {
      if (!this.canEdit) return;
      this.$emit('edit', this.product);
    },
    emitView() {
      this.$emit('view', this.product);
    },
    emitFinalizeDraft() {
      this.$emit('finalize', this.product);
    },
  },
};
</script>

<style scoped>
.product-card {
  border: none;
}
</style>
